import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { signInWithCustomToken } from "firebase/auth";
import { Timestamp } from "firebase/firestore";
import { auth } from "../../../../../config/firebaseConfig";
import { login } from "../../../../../redux/features/authSlice";
import dbService from "../../../../../firebase/dbService";
import envConfig from "../../../../../config/envConfig";
import { validateEmail, validatePhone, formatToE164 } from "../../../../../assets/js/validation";
import { toast } from "react-toastify";

import IndividualAuthHeader from "./IndividualAuthHeader";
import ParentStudentSwitch from "./ParentStudentSwitch";
import IndividualPhoneAuth from "./IndividualPhoneAuth";
import TermsPolicy from "./TermsPolicy";
import OtherAuth from "./OtherAuth";
import OtpForm from "./OtpForm";
import DetailsRegistrationForm from "./DetailsRegistrationForm";
import GooglePhoneForm from "./GooglePhoneForm";

const IndividualPhoneFlow = ({ getDashboardTargetUrl }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { schoolId } = useParams();

  const isLoginMode = location.pathname.startsWith("/login");

  const [stage, setStage] = useState("phone");
  const [registerAs, setRegisterAs] = useState("parent");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [googleUser, setGoogleUser] = useState(null);
  const [isFromLoginRedirect, setIsFromLoginRedirect] = useState(false);
  const [emailExistsMode, setEmailExistsMode] = useState(false);

  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showOtherLoginWays, setShowOtherLoginWays] = useState(false);
  const [error, setError] = useState(null);

  // Restore state from sessionStorage on mount
  useEffect(() => {
    const preVerifiedPhone = sessionStorage.getItem("verifiedPhoneNumber");
    if (preVerifiedPhone) {
      setPhoneNumber(preVerifiedPhone);
      setStage("details");
      setIsFromLoginRedirect(true);
      sessionStorage.removeItem("verifiedPhoneNumber");
      return;
    }

    const savedStage = sessionStorage.getItem("practido_signup_stage");
    const savedRole = sessionStorage.getItem("practido_signup_role");
    const savedPhone = sessionStorage.getItem("practido_signup_phone");

    if (savedStage === "details" && savedPhone) {
      setStage("details");
      setRegisterAs(savedRole || "parent");
      setPhoneNumber(savedPhone);
    }
  }, []);

  // Clean form state reset whenever route changes
  useEffect(() => {
    setStage("phone");
    setError(null);
    setOtpError("");
    setOtpCode("");
    setLoading(false);
    setOtpLoading(false);
    setEmailExistsMode(false);
    setPassword("");
    setName("");
  }, [location.pathname]);

  const resetFieldError = (field) => {
    setError((prev) => {
      if (!prev) return null;
      if (field === "email" || field === "emailCollision") {
        const next = { ...prev };
        delete next.email;
        delete next.emailCollision;
        return Object.keys(next).length > 0 ? next : null;
      }
      return { ...prev, [field]: null };
    });
  };

  const handleEditPhoneNumber = () => {
    setPhoneNumber((prev) => (prev ? String(prev).replace(/^\+91/, "").replace(/\D/g, "") : ""));
    setStage("phone");
    setOtpError("");
    setOtpCode("");
    setError(null);
    setOtpLoading(false);
  };

  const handleSignInExisting = () => {
    sessionStorage.removeItem("practido_signup_stage");
    sessionStorage.removeItem("practido_signup_role");
    sessionStorage.removeItem("practido_signup_phone");
    sessionStorage.removeItem("verifiedPhoneNumber");
    setPhoneNumber("");
    setStage("phone");
    setError(null);
    setEmailExistsMode(false);
    setIsFromLoginRedirect(false);
    navigate("/login" + (location.search || ""));
  };

  // Step 1: Send OTP
  const sendOtp = async (phoneToUse) => {
    setOtpError("");
    const response = await fetch(`${envConfig.backendUrl}/sendWhatsappOtpV1`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        phoneNumber: phoneToUse,
        purpose: isLoginMode ? "Login" : "Registration",
      }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to send OTP.");
    }
    return data;
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setOtpError("");

    const cleanPhone = phoneNumber.trim();
    if (!cleanPhone) {
      setError({ phone: "Phone number is required." });
      return;
    }
    if (!validatePhone(cleanPhone)) {
      setError({ phone: "Please enter a valid 10 digit phone number." });
      return;
    }

    const e164Phone = formatToE164(cleanPhone);
    setPhoneNumber(e164Phone);
    setLoading(true);
    try {
      await sendOtp(e164Phone);
      setStage("otp");
    } catch (err) {
      setError({ phone: err.message });
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleOtpSubmit = async (e, code) => {
    e.preventDefault();
    setOtpError("");
    setOtpLoading(true);

    try {
      const e164Phone = formatToE164(phoneNumber);
      const otpResponse = await fetch(`${envConfig.backendUrl}/verifyWhatsappOtpV1`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phoneNumber: e164Phone, otp: code }),
      });
      const otpData = await otpResponse.json();
      if (!otpResponse.ok) {
        throw new Error(otpData.message || "Invalid OTP.");
      }

      setOtpCode(code);

      const statusResponse = await fetch(`${envConfig.backendUrl}/phoneLoginV1`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phoneNumber: e164Phone }),
      });
      const statusData = await statusResponse.json();
      if (!statusResponse.ok) {
        throw new Error(statusData.message || "Failed to check user status.");
      }

      if (statusData.isNewUser === false && statusData.customToken) {
        await signInWithCustomToken(auth, statusData.customToken);
        await dispatch(login({})).unwrap().catch(() => null);
        const targetUrl = getDashboardTargetUrl ? getDashboardTargetUrl() : "/dashboard";
        navigate(targetUrl);
      } else {
        setStage("details");
        if (isLoginMode) {
          toast.info("No account found for this number. Please complete your registration.");
          setIsFromLoginRedirect(true);
        } else {
          sessionStorage.setItem("practido_signup_stage", "details");
          sessionStorage.setItem("practido_signup_role", registerAs);
          sessionStorage.setItem("practido_signup_phone", phoneNumber);
        }
      }
    } catch (err) {
      setOtpError(err.message);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleDetailsGoBack = () => {
    setStage("phone");
    sessionStorage.setItem("registration_stage", "phone");
  };

  // Step 3: Complete Sign Up
  const handleDetailsSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const cleanName = name.trim();
    const cleanEmail = email.trim();

    if (emailExistsMode) {
      if (!password) {
        setError({ password: "Password is required." });
        return;
      }

      setLoading(true);
      try {
        const apiKey = auth?.app?.options?.apiKey || envConfig.firebaseApiKey;
        const verifyRes = await fetch(
          `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${apiKey}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: cleanEmail,
              password: password,
              returnSecureToken: true,
            }),
          }
        );
        const verifyData = await verifyRes.json();

        if (!verifyRes.ok || !verifyData.idToken) {
          setError({ password: "Incorrect password. Please try again." });
          setLoading(false);
          return;
        }

        const idToken = verifyData.idToken;

        const linkResponse = await fetch(`${envConfig.backendUrl}/linkPhoneToExistingAccountWebV1`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            phoneNumber: formatToE164(phoneNumber),
            name: cleanName || undefined,
            role: registerAs,
          }),
        });
        const linkData = await linkResponse.json();
        if (!linkResponse.ok) {
          throw new Error(linkData.message || "Failed to link phone number.");
        }

        await signInWithCustomToken(auth, linkData.customToken);
        await dispatch(login({})).unwrap().catch(() => null);

        sessionStorage.removeItem("practido_signup_stage");
        sessionStorage.removeItem("practido_signup_role");
        sessionStorage.removeItem("practido_signup_phone");

        const targetUrl = getDashboardTargetUrl ? getDashboardTargetUrl() : "/dashboard";
        navigate(targetUrl);
      } catch (err) {
        setError({ password: "Incorrect password. Please try again." });
      } finally {
        setLoading(false);
      }
      return;
    }

    const errors = {};
    if (!cleanName) errors.name = "Name is required.";
    if (!cleanEmail) errors.email = "Email is required.";
    else if (!validateEmail(cleanEmail)) errors.email = "Please enter a valid email id.";

    if (Object.keys(errors).length > 0) {
      setError(errors);
      return;
    }

    setLoading(true);
    try {
      const emailCheckResponse = await fetch(`${envConfig.backendUrl}/checkEmailExistsWebV1`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: cleanEmail }),
      });
      const emailCheckData = await emailCheckResponse.json();

      if (emailCheckData.exists) {
        if (emailCheckData.hasPhone || emailCheckData.provider === "google.com") {
          let existingPhone =
            emailCheckData.phone ||
            emailCheckData.phoneNumber ||
            emailCheckData.linkedPhone ||
            emailCheckData.userPhone ||
            emailCheckData.maskedPhone ||
            emailCheckData.data?.phone ||
            emailCheckData.data?.phoneNumber ||
            emailCheckData.user?.phone ||
            emailCheckData.user?.phoneNumber ||
            "";

          if (!existingPhone) {
            try {
              const { collection, query, where, getDocs } = await import("firebase/firestore");
              const userQuery = query(collection(dbService.db, "user"), where("email", "==", cleanEmail));
              const userSnap = await getDocs(userQuery);
              if (!userSnap.empty) {
                const uData = userSnap.docs[0].data();
                existingPhone = uData?.phone || uData?.phoneNumber || "";
              }
              if (!existingPhone) {
                const parentQuery = query(collection(dbService.db, "parents"), where("email", "==", cleanEmail));
                const parentSnap = await getDocs(parentQuery);
                if (!parentSnap.empty) {
                  const pData = parentSnap.docs[0].data();
                  existingPhone = pData?.phone || pData?.phoneNumber || "";
                }
              }
            } catch (e) {
              console.log("Error querying phone from Firestore:", e);
            }
          }

          const digits = String(existingPhone).replace(/\D/g, "");
          const last4 = digits ? digits.slice(-4) : (typeof existingPhone === "string" && existingPhone.length >= 4 ? existingPhone.slice(-4) : "");
          const maskedPhone = last4 ? `xxxxxx${last4}` : (typeof emailCheckData.maskedPhone === "string" ? emailCheckData.maskedPhone : "");

          setError({
            emailCollision: {
              email: cleanEmail,
              maskedPhone: maskedPhone,
              provider: emailCheckData.provider,
            },
          });
          setLoading(false);
          return;
        }

        if (emailCheckData.provider === "password") {
          setEmailExistsMode(true);
          setLoading(false);
          toast.info("This email already has an account. Please enter your password to link your phone number.");
          return;
        }
      }

      const e164Phone = formatToE164(phoneNumber);
      const response = await fetch(`${envConfig.backendUrl}/registerUserWebV1`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phoneNumber: e164Phone,
          name: cleanName,
          email: cleanEmail,
          role: registerAs,
          schoolId: schoolId || null,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Registration failed.");
      }

      await signInWithCustomToken(auth, data.customToken);
      await dispatch(login({})).unwrap().catch(() => null);

      sessionStorage.removeItem("practido_signup_stage");
      sessionStorage.removeItem("practido_signup_role");
      sessionStorage.removeItem("practido_signup_phone");

      const targetUrl = getDashboardTargetUrl ? getDashboardTargetUrl() : "/dashboard";
      navigate(targetUrl);
    } catch (err) {
      setError({ other: err.message });
    } finally {
      setLoading(false);
    }
  };

  // Google Sign-Up Flow
  const handleGoogleSignUp = async () => {
    setError(null);
    setGoogleLoading(true);
    try {
      const { signInWithPopup, GoogleAuthProvider } = await import("firebase/auth");
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (err) {
      setError({ other: err.message || "Google Sign-Up failed." });
      setGoogleLoading(false);
    }
  };

  // Firebase auth state listener for Google Signups
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user || !user.email) return;

      const isGoogleUser = user?.providerData?.some((p) => p.providerId === "google.com");
      if (isGoogleUser) {
        setGoogleLoading(true);
        try {
          const profile = await dbService.getAuthDetails("user", user.uid).catch(() => null);
          if (!profile) {
            // New Google user -> initialize Firestore user and parents docs
            const userDoc = {
              uid: user.uid,
              email: user.email || "",
              name: user.displayName || "",
              phone: "",
              phoneVerified: false,
              accountType: "parent",
              createdAt: Timestamp.now(),
            };
            await dbService.createDocumentWithId("user", userDoc, user.uid);

            const roleDocData = {
              uid: user.uid,
              email: user.email || "",
              name: user.displayName || "",
              phone: "",
              phoneVerified: false,
              accountType: "parent",
              createdAt: Timestamp.now(),
            };
            if (schoolId) {
              roleDocData.schoolId = schoolId;
            }
            await dbService.createDocumentWithId("parents", roleDocData, user.uid);

            // Flag dashboard to auto-open WhatsApp phone verification dialog on first load
            sessionStorage.setItem("open_verify_phone_modal_on_load", "true");
          }

          // Hydrate Redux state and redirect to dashboard
          await dispatch(login({ email: user.email || "", password: "" })).unwrap().catch(() => null);
          const targetUrl = getDashboardTargetUrl ? getDashboardTargetUrl() : "/dashboard";
          navigate(targetUrl);
        } catch (e) {
          console.error("Error with Google user:", e);
        } finally {
          setGoogleLoading(false);
        }
      }
    });

    return () => unsubscribe();
  }, [dispatch, navigate, schoolId, getDashboardTargetUrl]);

  if (stage === "otp") {
    return (
      <div className="w-full h-full flex flex-col justify-start">
        <OtpForm
          phoneNumber={phoneNumber}
          handleOtpSubmit={handleOtpSubmit}
          sendOtp={() => sendOtp(phoneNumber)}
          otpLoading={otpLoading}
          otpError={otpError}
          setOtpError={setOtpError}
          handleEditPhoneNumber={handleEditPhoneNumber}
        />
      </div>
    );
  }

  if (stage === "details") {
    return (
      <DetailsRegistrationForm
        emailExistsMode={emailExistsMode}
        setEmailExistsMode={setEmailExistsMode}
        isFromLoginRedirect={isFromLoginRedirect}
        registerAs={registerAs}
        setRegisterAs={setRegisterAs}
        name={name}
        setName={setName}
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        handleDetailsSubmit={handleDetailsSubmit}
        handleDetailsGoBack={handleDetailsGoBack}
        handleSignInExisting={handleSignInExisting}
        loading={loading}
        error={error}
        setError={setError}
        resetFieldError={resetFieldError}
      />
    );
  }

  return (
    <>
      <IndividualAuthHeader
        title={isLoginMode ? "Sign In to Practido" : "Enter Phone to Register"}
        subtitle={
          isLoginMode
            ? "Sign in to access your dashboard"
            : "Sign up to continue your learning journey"
        }
      />

      {/* TODO: Re-activate role switcher in future releases
      {!isLoginMode && (
        <ParentStudentSwitch
          registerAs={registerAs}
          setRegisterAs={setRegisterAs}
          labels={{ parent: "I'm Parent", student: "I'm Student" }}
        />
      )}
      */}

      <IndividualPhoneAuth
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        handlePhoneSubmit={handlePhoneSubmit}
        loading={loading}
        error={error}
        resetFieldError={resetFieldError}
      />

      <TermsPolicy />

      <OtherAuth
        isLoginMode={isLoginMode}
        showOtherLoginWays={showOtherLoginWays}
        setShowOtherLoginWays={setShowOtherLoginWays}
        handleGoogleSignUp={handleGoogleSignUp}
        handleEmailAuthClick={() =>
          navigate(isLoginMode ? "/login/email" + (location.search || "") : "/register/email" + (location.search || ""))
        }
        handleSchoolClick={() => navigate(isLoginMode ? "/login/school" : "/register/school")}
        loading={loading}
        googleLoading={googleLoading}
      />
    </>
  );
};

export default IndividualPhoneFlow;
